// This is a placeholder for the HowToJoinNewSection component.
// The implementation will be added later.

import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import arrow from 'assets/images/arrow.png';
import greenHeader from 'assets/how-to-join-headers/green.png';
import purpleHeader from 'assets/how-to-join-headers/purple.png';
import { generateTestId } from '../../../app/functions';
import HtmlContent from 'components/html/HtmlContent';

const SectionWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    padding: '32px 0',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '140px',
    alignSelf: 'stretch',
    background: theme.palette.background.lightBlue,
    [theme.breakpoints.up('sm')]: {
        padding: '40px 0',
    },
    [theme.breakpoints.up('md')]: {
        padding: '40px 0',
    },
    [theme.breakpoints.up('lg')]: {
        padding: '50px 0',
    },
    [theme.breakpoints.up('xl')]: {
        padding: '60px 0',
    },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    padding: '0 24px',
    alignSelf: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '40px',
    [theme.breakpoints.up('sm')]: {
        padding: '0 12px',
    },
    [theme.breakpoints.up('md')]: {
        padding: '0 16px',
    },
    [theme.breakpoints.up('lg')]: {
        padding: '0 20px',
    },
    [theme.breakpoints.up('xl')]: {
        maxWidth: '1400px',
    },
}));

const Header = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    textAlign: 'center',
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    textTransform: 'capitalize',
    [theme.breakpoints.up('sm')]: {
        fontSize: '32px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '36px',
    },
    [theme.breakpoints.up('lg')]: {
        fontSize: '40px',
    },
}));

const CardsWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '60px',
    alignSelf: 'stretch',
    [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '40px',
    },
    [theme.breakpoints.up('md')]: {
        gap: '60px',
    },
    [theme.breakpoints.up('lg')]: {
        gap: '60px',
    },
}));

const StyledCard = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '300px',
    padding: '60px 20px 24px 20px',
    alignItems: 'center',
    borderRadius: '24px',
    background: theme.palette.common.white,
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
        width: '250px',
        padding: '40px 15px 20px 15px',
        minHeight: 'fit-content',
    },
    [theme.breakpoints.up('md')]: {
        width: '350px',
        padding: '40px 15px 20px 15px',
        minHeight: 'fit-content',
    },
    [theme.breakpoints.up('lg')]: {
        width: '450px',
        padding: '40px 40px 40px 40px',
        minHeight: 'fit-content',
    },
    [theme.breakpoints.up('xl')]: {
        width: '600px',
        minHeight: 'fit-content',
    },
}));

const StyledImage = styled('img')(({ theme }) => ({
    width: '110%',
    height: '100px',
    zIndex: 1,
    position: 'absolute',
    top: '-40px',
    left: 0,
    [theme.breakpoints.up('sm')]: {
        top: '-40px',
        left: 0,
    },
    [theme.breakpoints.up('md')]: {
        top: '-38px',
        left: 0,
    },
    [theme.breakpoints.up('lg')]: {
        top: '-40px',
        left: 0,
    },
    [theme.breakpoints.up('xl')]: {
        top: '-37px',
        left: 0,
    },
    '&.first-image': {
        top: 0,
        left: '-30px',
        [theme.breakpoints.up('sm')]: {
            top: 0,
            left: '-25px',
        },
        [theme.breakpoints.up('md')]: {
            top: 0,
            left: '-35px',
        },
        [theme.breakpoints.up('lg')]: {
            top: 0,
            left: '-45px',
        },
        [theme.breakpoints.up('xl')]: {
            top: 0,
            left: '-60px',
        },
    },
}));

const CardContentHeader = styled(Typography)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    color: theme.palette.howToJoinSection.cardContentHeader,
    direction: 'right',
    gap: '10px',
    fontSize: '18px',
    fontStyle: 'normal',
    textAlign: 'right',
    direction: 'rtl',
    fontWeight: 700,
    lineHeight: 'normal',
    textTransform: 'capitalize',
    marginBottom: '10px',
    marginTop: '20px',
    [theme.breakpoints.only('sm')]: {
        fontSize: '16px',
        gap: '0px',
        marginTop: '0px',
        marginLeft: '10px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '18px',
        gap: '80px',
        marginTop: '0px',
    },
    [theme.breakpoints.up('lg')]: {
        fontSize: '20px',
        gap: '80px',
        marginTop: '0px',
    },
    [theme.breakpoints.up('xl')]: {
        marginTop: '40px',
    },
    '&.first-header': {
        marginTop: '24px',
        [theme.breakpoints.only('sm')]: {
            marginTop: '0px',
        },
        [theme.breakpoints.up('md')]: {
            marginTop: '20px',
        },
        [theme.breakpoints.up('lg')]: {
            marginTop: '20px',
        },
        [theme.breakpoints.up('xl')]: {
            marginTop: '42px',
        },
    }
}));

const HeaderText = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.white,
    textAlign: 'center',
    fontSize: '21px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    textTransform: 'capitalize',
    top: '50%',
    left: '50%',
    marginTop: '-60px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '20px',
        marginTop: '-40px',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '22px',
        marginTop: '-38px',
    },
    [theme.breakpoints.up('lg')]: {
        fontSize: '24px',
        marginTop: '-45px',
    },
    [theme.breakpoints.up('xl')]: {
        fontSize: '28px',
        marginTop: '-33px'
    },
    '&.first-header': {
        marginTop: '-40px',
        [theme.breakpoints.up('sm')]: {
            marginTop: '-35px',
        },
        [theme.breakpoints.up('md')]: {
            marginTop: '-30px',
        },
    }
}));

const JoinButton = styled('button')(({ theme }) => ({
    display: 'flex',
    padding: '12px 40px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    alignSelf: 'center',
    borderRadius: '12px',
    background: theme.palette.howToJoinSection.joinButton,
    color: theme.palette.common.white,
    textAlign: 'right',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    textTransform: 'capitalize',
    border: 'none',
    cursor: 'pointer',
    width: '260px',
    height: '41px',
    borderRadius: '12px',
    marginTop: '20px',
    [theme.breakpoints.up('sm')]: {
        width: '200px',
        height: '41px',
    },
    [theme.breakpoints.up('md')]: {
        width: '220px',
        height: '44px',
        borderRadius: '24px',
    },
    [theme.breakpoints.up('lg')]: {
        width: '206px',
        height: '46px',
    }
}));

const CardContentContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    direction: 'rtl',
    textAlign: 'right',
    width: '100%',
    width: '260px',
    
    [theme.breakpoints.up('sm')]: {
        width: '240px',
        marginTop: '10px',
    },
    [theme.breakpoints.up('md')]: {
        width: '260px',
        marginTop: '15px',
        marginLeft: '20px',
    },
    [theme.breakpoints.up('lg')]: {
        width: '320px',
    },
    [theme.breakpoints.up('xl')]: {
        width: '350px',
    },
}));

const StyledParagraph = styled('p')(({ theme }) => ({
    marginTop: '5px',
    paddingTop: '0px',
    margin: '0 auto',
    textAlign: 'left',
    direction: 'ltr',
    [theme.breakpoints.only('sm')]: {
        marginTop: '10px',
    },
}));

const CardHtmlContent = styled('div')(({ theme }) => ({
    direction: 'ltr',
    textAlign: 'left',
    [theme.breakpoints.only('sm')]: {
        marginTop: '10px',
        marginLeft: '10px',
    },
}));


const HowToJoinSection = ({data, joinButtonText, onClick, testID}) => {
    if(!data?.cards?.length) return null;
    const theme = useTheme();

    return (
        <SectionWrapper testID={generateTestId(testID, 'section-wrapper')}>
            <ContentWrapper testID={generateTestId(testID, 'content-wrapper')}>
                <Header testID={generateTestId(testID, 'header')}>
                    <Typography variant="sectionTitle">{data.title}</Typography>
                </Header>
                <CardsWrapper testID={generateTestId(testID, 'cards-wrapper')}>
                    {data.cards.map((card, index) => (
                        <MainCard 
                            key={index} 
                            card={card}
                            joinButtonText={joinButtonText}
                            sampleContent={<HtmlContent content={card.htmlText} />} 
                            position={index + 1} 
                            onClick={onClick} 
                            headerText={card.title}
                            cardContentHeader={card.subtitle}
                            testID={generateTestId(testID, `card-${index + 1}`)}
                        />
                    ))}
                </CardsWrapper>
            </ContentWrapper>
        </SectionWrapper>
    );
};

export default HowToJoinSection; 

function MainCard({ sampleContent, position, onClick, headerText, testID, joinButtonText, cardContentHeader }) {
    const [isTextVisible, setIsTextVisible] = useState({ 1: false, 2: false });
    const isColumnCards = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const toggleTextVisibility = (pos) => {
        setIsTextVisible(prevState => ({
            ...prevState,
            [pos]: !prevState[pos]
        }));
    };

    const source = position === 1 ? greenHeader : purpleHeader;

    return (
        <StyledCard testID={generateTestId(testID, `card-${position}`)}>
            <StyledImage 
                src={source}                    
                alt={`header-${position}`} 
                className={position === 1 ? 'first-image' : ''}
                testID={generateTestId(testID, `header-image-${position}`)} 
            />
            <HeaderText 
                className={position === 1 ? 'first-header' : ''} 
                style={{ zIndex: 2 }} 
                testID={generateTestId(testID, `header-text-${position}`)}
            >
                {headerText}
            </HeaderText>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <CardContentContainer testID={generateTestId(testID, `card-content-${position}-container`)}>
                    <CardContentHeader 
                        component="div"
                        className={position === 1 ? 'first-header' : ''} 
                        style={{ zIndex: 2, textAlign: 'right', direction: 'rtl', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                        testID={generateTestId(testID, `card-content-header-${position}`)}
                    >
                        {isColumnCards && <StyledParagraph testID={generateTestId(testID, `card-content-header-text-${position}`)}>
                            {headerText || cardContentHeader}
                        </StyledParagraph>}
                        {isColumnCards && (
                            <img 
                                src={arrow} 
                                alt="toggle" 
                                onClick={() => toggleTextVisibility(position)} 
                                style={{ cursor: 'pointer', marginLeft: '5px', width: '32px', height: '32px', transform: isTextVisible[position] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', margin: '0 auto' }} 
                                testID={generateTestId(testID, `arrow-toggle-${position}`)} 
                            />
                        )}
                    </CardContentHeader>
                    <CardHtmlContent testID={generateTestId(testID, `card-content-text-${position}`)}>
                        {isColumnCards && isTextVisible[position] && sampleContent || !isColumnCards && sampleContent}
                    </CardHtmlContent>
                </CardContentContainer>
            </Box>
            <JoinButton 
                testID={generateTestId(testID, `join-button-${position}`)}
                onClick={onClick}
            >
                {joinButtonText}
            </JoinButton>
        </StyledCard>
    );
}